import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: '⬡',
      title: 'Projects & Sprints',
      desc: 'Plan work in sprints with drag-and-drop boards, milestone tracking, and burndown charts — all in one view.',
    },
    {
      icon: '◈',
      title: 'Task Management',
      desc: 'Assign tasks, set priorities, track time logs, and watch progress update in real time across your whole team.',
    },
    {
      icon: '⟡',
      title: 'Team Availability',
      desc: 'See who is free, who is in a meeting, and who is on leave — then schedule work around your actual people.',
    },
    {
      icon: '◎',
      title: 'CRM Built In',
      desc: 'Customers, leads, and deals live alongside your projects, so sales and delivery always share the same truth.',
    },
    {
      icon: '⬢',
      title: 'Real-time Chat',
      desc: 'Threaded conversations tied to projects and tasks. No context lost, no tab switching, no noise.',
    },
    {
      icon: '◉',
      title: 'Role-based Access',
      desc: 'Admins, managers, members — each role sees exactly what they need and nothing they don\'t.',
    },
  ];

  const stats = [
    { value: '10×', label: 'faster sprint setup' },
    { value: '100%', label: 'role-controlled access' },
    { value: '∞', label: 'projects, one platform' },
  ];

  return (
    <div className="lp">
      {/* Background mesh */}
      <div className="lp__mesh" aria-hidden="true">
        <div className="lp__mesh-orb lp__mesh-orb--1" />
        <div className="lp__mesh-orb lp__mesh-orb--2" />
        <div className="lp__mesh-orb lp__mesh-orb--3" />
        <div className="lp__grid" />
      </div>

      {/* NAV */}
      <nav className="lp__nav">
        <div className="lp__nav-inner">
          <div className="lp__brand">
            <div className="lp__logo-mark">PM</div>
            <span className="lp__brand-name">Aminoid</span>
          </div>
          <div className="lp__nav-links">
            <a href="#features" className="lp__nav-link">Features</a>
            <a href="#why" className="lp__nav-link">Why Us</a>
            <button className="lp__nav-cta" onClick={() => navigate('/login')}>
              Sign in
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="lp__hero" ref={heroRef}>
        <div className="lp__hero-inner">
          <div className="lp__hero-eyebrow">
            <span className="lp__eyebrow-dot" />
            Project Management · CRM · Team OS
          </div>

          <h1 className="lp__hero-title">
            Ship faster.<br />
            <span className="lp__gradient-text">Together.</span>
          </h1>

          <p className="lp__hero-subtitle">
            One platform for projects, sprints, tasks, team chat, availability, customers, leads, and deals.
            Stop switching tabs. Start shipping.
          </p>

          <div className="lp__hero-actions">
            <button className="lp__btn-primary" onClick={() => navigate('/login')}>
              <span>Get started free</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="lp__btn-ghost" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>
              See what's inside
            </button>
          </div>

          {/* Stats row */}
          <div className="lp__stats">
            {stats.map((s, i) => (
              <div className="lp__stat" key={i}>
                <div className="lp__stat-value">{s.value}</div>
                <div className="lp__stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Hero dashboard mockup */}
        <div className="lp__hero-visual">
          <div className="lp__mockup">
            <div className="lp__mockup-bar">
              <span className="lp__mockup-dot lp__mockup-dot--r" />
              <span className="lp__mockup-dot lp__mockup-dot--y" />
              <span className="lp__mockup-dot lp__mockup-dot--g" />
              <span className="lp__mockup-title">Dashboard · Aminoid PM</span>
            </div>
            <div className="lp__mockup-body">
              <div className="lp__mockup-sidebar">
                {['Dashboard', 'Projects', 'Tasks', 'Board', 'Sprints', 'Chat', 'Availability', 'Customers', 'Leads'].map((item, i) => (
                  <div className={`lp__mockup-nav-item ${i === 0 ? 'lp__mockup-nav-item--active' : ''}`} key={item}>
                    <div className="lp__mockup-nav-dot" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="lp__mockup-content">
                <div className="lp__mockup-header">
                  <div className="lp__mockup-heading">Overview</div>
                  <div className="lp__mockup-badge">Live</div>
                </div>
                <div className="lp__mockup-cards">
                  {[
                    { label: 'Active Projects', val: '12', color: 'var(--accent)' },
                    { label: 'Open Tasks', val: '48', color: 'var(--accent2)' },
                    { label: 'Team Online', val: '7', color: 'var(--accent3)' },
                  ].map(c => (
                    <div className="lp__mockup-card" key={c.label}>
                      <div className="lp__mockup-card-val" style={{ color: c.color }}>{c.val}</div>
                      <div className="lp__mockup-card-label">{c.label}</div>
                    </div>
                  ))}
                </div>
                <div className="lp__mockup-chart">
                  {[60, 80, 50, 90, 70, 95, 65, 85].map((h, i) => (
                    <div className="lp__mockup-bar-wrap" key={i}>
                      <div className="lp__mockup-bar" style={{ height: `${h}%` }} />
                    </div>
                  ))}
                </div>
                <div className="lp__mockup-tasks">
                  {['Design system audit', 'Sprint 4 planning', 'API integration', 'Deploy review'].map((t, i) => (
                    <div className="lp__mockup-task" key={t}>
                      <div className={`lp__mockup-task-check ${i < 2 ? 'lp__mockup-task-check--done' : ''}`} />
                      <div className="lp__mockup-task-text">{t}</div>
                      <div className="lp__mockup-task-pill" style={{ background: ['#7c6aff22', '#ff6b8a22', '#7c6aff22', '#34d39922'][i], color: ['var(--accent)', 'var(--accent2)', 'var(--accent)', 'var(--accent3)'][i] }}>
                        {['Done', 'Done', 'In Progress', 'Review'][i]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="lp__section" id="features">
        <div className="lp__section-inner">
          <div className="lp__section-label">Everything you need</div>
          <h2 className="lp__section-title">
            One platform.<br />No duct tape.
          </h2>
          <p className="lp__section-sub">
            Built for teams that ship real products — not for spreadsheet managers.
          </p>

          <div className="lp__features-grid">
            {features.map((f, i) => (
              <div className="lp__feature-card" key={i}>
                <div className="lp__feature-icon">{f.icon}</div>
                <h3 className="lp__feature-title">{f.title}</h3>
                <p className="lp__feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY SECTION */}
      <section className="lp__section lp__section--alt" id="why">
        <div className="lp__section-inner lp__why-grid">
          <div className="lp__why-text">
            <div className="lp__section-label">Built different</div>
            <h2 className="lp__section-title lp__section-title--left">
              Your entire team, in perfect sync.
            </h2>
            <p className="lp__section-sub lp__section-sub--left">
              Most tools make you choose between project management and CRM. Aminoid does both —
              so your delivery team and your sales team always work from the same reality.
            </p>
            <ul className="lp__why-list">
              {[
                'Sprints tied to real customer deals',
                'Tasks linked to leads and activities',
                'One login, all roles covered',
                'Notifications that actually matter',
                'Time tracking built in, not bolted on',
              ].map(item => (
                <li className="lp__why-item" key={item}>
                  <svg className="lp__check" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <circle cx="9" cy="9" r="9" fill="rgba(124,106,255,0.15)" />
                    <path d="M5.5 9l2.5 2.5 4.5-5" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
            <button className="lp__btn-primary" onClick={() => navigate('/login')}>
              <span>Start for free</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className="lp__why-visual">
            <div className="lp__pipeline">
              {[
                { color: 'var(--accent)', label: 'Lead captured', sub: 'From CRM module', icon: '◎' },
                { color: 'var(--accent4)', label: 'Deal created', sub: 'Auto-linked to project', icon: '⬡' },
                { color: 'var(--accent2)', label: 'Sprint planned', sub: 'Team assigned', icon: '◈' },
                { color: 'var(--accent3)', label: 'Tasks delivered', sub: '100% on time', icon: '◉' },
              ].map((step, i) => (
                <div className="lp__pipeline-step" key={i}>
                  <div className="lp__pipeline-icon" style={{ color: step.color, borderColor: step.color + '44', background: step.color + '11' }}>
                    {step.icon}
                  </div>
                  <div className="lp__pipeline-info">
                    <div className="lp__pipeline-label">{step.label}</div>
                    <div className="lp__pipeline-sub">{step.sub}</div>
                  </div>
                  {i < 3 && <div className="lp__pipeline-connector" style={{ borderColor: step.color + '33' }} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="lp__cta">
        <div className="lp__cta-inner">
          <div className="lp__cta-glow" />
          <div className="lp__section-label lp__section-label--center">Ready to ship?</div>
          <h2 className="lp__cta-title">
            Your team is waiting.<br />
            <span className="lp__gradient-text">Start today.</span>
          </h2>
          <p className="lp__cta-sub">
            No credit card. No 30-page setup guide. Just sign in and go.
          </p>
          <button className="lp__btn-primary lp__btn-primary--large" onClick={() => navigate('/login')}>
            <span>Open the app</span>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 9h12M10 4l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp__footer">
        <div className="lp__footer-inner">
          <div className="lp__brand">
            <div className="lp__logo-mark lp__logo-mark--sm">PM</div>
            <span className="lp__brand-name">Aminoid</span>
          </div>
          <p className="lp__footer-copy">
            <a href="https://www.aminoid.in/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>Powered by Aminoid.</a>
          </p>
        </div>
      </footer>
    </div>
  );
}